import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Reveals its content as it scrolls into view — a rise, with an optional fade.
 *
 * The whole design here is dictated by one constraint: this page is prerendered
 * to static HTML. A reveal built the usual way — start at `opacity: 0`, add a
 * class from JS — would ship a snapshot in which the entire page is invisible,
 * which is the worst possible outcome for the surface whose reason to exist is
 * being crawlable.
 *
 * So the resting state is VISIBLE and JS only ever takes visibility away:
 *
 *  - Server render, no JS, or no IntersectionObserver → nothing happens and the
 *    content is simply there. The snapshot is complete.
 *  - On the client, a layout effect (before paint, so nothing flickers) arms
 *    only the elements still BELOW the fold. Anything already on screen is
 *    left alone rather than being hidden and faded back in.
 *  - Reduced motion → not armed at all, and the CSS neutralises the pending
 *    state too in case anything slips through.
 *
 * It also drives the DOM directly instead of through state: there are several
 * dozen of these on the page and none of them need to re-render to animate.
 */

/** `useLayoutEffect` warns when it runs during the prerender; effects don't run
    there at all, so falling back to `useEffect` keeps the console clean. */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type RevealTag = 'div' | 'li' | 'blockquote';

interface RevealProps {
  children: ReactNode;
  /** Element to render. Defaults to a div. */
  as?: RevealTag;
  className?: string;
  style?: CSSProperties;
  /** Milliseconds to wait before this one starts — used to stagger a list. */
  delay?: number;
  /** How far it rises, in pixels. */
  distance?: number;
  /** Rise without fading. The mock internals move further and stay opaque. */
  fade?: boolean;
}

export function Reveal({
  children,
  as: Tag = 'div',
  className = '',
  style,
  delay = 0,
  distance = 24,
  fade = true,
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Old browsers and the test environment simply keep the content visible.
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Arm only what is still below the fold. Hiding something the visitor can
    // already see would blink it out and back in.
    if (element.getBoundingClientRect().top < window.innerHeight) return;

    element.dataset.reveal = 'pending';

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          delete element.dataset.reveal;
          observer.disconnect();
        }
      },
      // Waits until the element is a little way in, so it animates while the
      // visitor is looking at it rather than at the very bottom edge.
      { rootMargin: '0px 0px -12% 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      className={`fias-reveal ${fade ? '' : 'fias-reveal-solid'} ${className}`}
      style={
        {
          '--fias-reveal-delay': `${delay}ms`,
          '--fias-reveal-y': `${distance}px`,
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </Tag>
  );
}
